import React, { PureComponent } from 'react'
import PropTypes from 'prop-types'

import {
  ButtonStyle,
  ConfirmationModal,
  ModalAction,
  ModalActionTypes,
  PromptConfig as PConfig,
} from '@acorns/web-components'
import debug from 'debug'
import { compose, getContext } from 'recompose'

import AlertBadgePath from 'assets/icons/alert-badge.png'
import { TestIDs } from 'test/utils/testIds'

type Props = {}

type State = {
  actions?: ModalAction[]
  message: string
  button: string
  buttonStyle: ButtonStyle
  closeModalCallback?: () => void
  openModalCallback?: () => void
  eventFields?: object
  title: string
  image: string
  show: boolean
}

export type PromptConfig = PConfig & {
  closeModalCallback?: () => void
  openModalCallback?: () => void
}

export type Prompt = (data: PromptConfig) => Promise<{
  index: number
  handler?: () => void
}>

export type PromptProps = {
  promptActionTypes: ModalActionTypes
  promptUser: Prompt
  prompted: boolean
}

// Registry for graphql error handlers
export class Provider extends PureComponent<Props, State> {
  public static childContextTypes = {
    promptUser: PropTypes.func.isRequired,
    promptActionTypes: PropTypes.object.isRequired,
  }

  protected log: debug
  protected pending: { resolve: any; reject: any } | null

  constructor(props) {
    super(props)

    this.pending = null

    // Note: Setup an internal logger
    this.log = debug('prompt')
    // Note: Setup our initial state
    this.state = {
      show: false,
      message: 'It looks like something went wrong.',
      title: 'Oops!',
      image: AlertBadgePath,
      button: 'Ok',
    }
  }

  get actions() {
    if (this.state.button) {
      return [
        {
          type: ModalActionTypes.primary,
          label: this.state.button,
          handler: this.onAcknowledge,
        },
      ]
    } else if (this.state.actions) {
      return this.state.actions.map((action, index) => ({
        ...action,
        handler: this.onAcknowledge.bind(this, {
          index,
          ...action,
        }),
      }))
    }

    throw new Error('Prompt must receive "button" or "actions" config')
  }

  public getChildContext() {
    const { promptUser } = this

    return {
      promptActionTypes: ModalActionTypes,
      promptUser,
    }
  }

  public onRequestClose = () => {
    this.setState({ show: false })
  }

  public promptUser = (config: PromptConfig) => {
    if (this.state.show === true) {
      this.log('Attempted to prompt user when prompt is already showing. This is a NO-OP.')
      return Promise.resolve()
    }

    this.setState({
      actions: config.actions,
      message: config.message,
      button: config.button,
      buttonStyle: config.buttonStyle,
      closeModalCallback: config.closeModalCallback,
      openModalCallback: config.openModalCallback,
      eventFields: config.eventFields,
      title: config.title,
      image: config.image === undefined ? AlertBadgePath : config.image,
      show: true,
    })

    return new Promise((resolve, reject) => {
      this.pending = { resolve, reject }
    })
  }

  public render() {
    // Ignoring the following logic for code coverage since it's utilizing a
    // third party package
    /* istanbul ignore next */
    return (
      <>
        {this.props.children}

        <ConfirmationModal
          onRequestClose={() => {
            this.onRequestClose()
            if (this.state.closeModalCallback) {
              this.state.closeModalCallback()
            }
          }}
          onOpenComplete={() => {
            if (this.state.openModalCallback) {
              this.state.openModalCallback()
            }
          }}
          isOpen={this.state.show}
          image={this.state.image}
          title={this.state.title}
          buttonStyle={this.state.buttonStyle}
          message={this.state.message}
          eventFields={this.state.eventFields}
          actions={this.actions}
          data-testid={TestIDs.UTILS.PROMPT_MODAL}
        />
      </>
    )
  }

  /* istanbul ignore next */
  private onAcknowledge = (event) => {
    this.setState({ show: false })
    this.pending.resolve(event)
    this.pending = null
  }
}

export const withPrompt = compose(
  getContext({
    promptActionTypes: PropTypes.object,
    promptUser: PropTypes.func,
  }),
)

export { ModalActionTypes }
